import type { DataTableEventV1ActiveUsers, DataTableEventV1SelectionChange } from '@/model/DataTableEvent.model';
import InVivoError from '@/model/InVivoError';
import type { StudyApiId } from '@/model/Study.model';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import {
  EMPTY,
  fromEvent,
  map,
  Observable,
  retry,
  startWith,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  timer,
} from 'rxjs';
import { type DataTableApiId, DataTableEvent } from '../DataTable.model';

const INITIAL_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;

export interface DataTableRealtimeServiceProps {
  studyApiId: StudyApiId;
  dataTableApiId: DataTableApiId;
  eventEmitter: EventTarget;
}

interface SseEvent {
  type: string;
  event: MessageEvent;
}

/**
 * Subscribes to the messaging service's per-datatable SSE stream and forwards
 * received events onto the shared DataTableService EventTarget so consumers
 * subscribe via the existing dataTableService.subscribe(...) API.
 *
 * Uses RxJS observables with exponential-backoff reconnection and
 * visibility-aware connection management. Routine EventSource `error` events
 * (the browser's own self-healing reconnect while CONNECTING) are ignored; only
 * a CLOSED connection errors the stream and triggers the backoff retry.
 */
export class DataTableRealtimeService {
  readonly #studyApiId: StudyApiId;
  readonly #dataTableApiId: DataTableApiId;
  readonly #eventEmitter: EventTarget;
  readonly #url: string;

  readonly #destroy$ = new Subject<void>();

  #subscription: Subscription | null = null;

  constructor({ studyApiId, dataTableApiId, eventEmitter }: DataTableRealtimeServiceProps) {
    this.#studyApiId = studyApiId;
    this.#dataTableApiId = dataTableApiId;
    this.#eventEmitter = eventEmitter;
    const path = `/api/messaging/v1/study/${studyApiId}/datatable/${dataTableApiId}/events`;
    this.#url = new URL(path, window.AppConfig.messagingApiUrl).href;
  }

  connect(): void {
    this.#subscription = this.#buildConnection$().subscribe({
      next: ({ type, event }) => {
        this.#handleEvent(type, event);
      },
    });
  }

  destroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
    this.#subscription?.unsubscribe();
    this.#subscription = null;
  }

  #createEventSource$(): Observable<SseEvent> {
    return new Observable<SseEvent>((subscriber) => {
      const eventSource = new EventSource(this.#url, { withCredentials: true });

      eventSource.addEventListener('selection_change', (messageEvent) => {
        subscriber.next({ type: 'selection_change', event: messageEvent as MessageEvent });
      });

      eventSource.addEventListener('active_users', (messageEvent) => {
        subscriber.next({ type: 'active_users', event: messageEvent as MessageEvent });
      });

      eventSource.addEventListener('error', () => {
        // EventSource fires `error` for routine, self-healing conditions (its own
        // reconnect while CONNECTING). Only a CLOSED connection is genuinely dead,
        // so only then do we error the stream and let the RxJS backoff rebuild it.
        if (eventSource.readyState === EventSource.CLOSED) {
          subscriber.error(new Error('EventSource closed'));
        }
      });

      return () => {
        eventSource.close();
      };
    });
  }

  #buildConnection$(): Observable<SseEvent> {
    const visibility$ = fromEvent(document, 'visibilitychange').pipe(
      map(() => document.visibilityState),
      startWith(document.visibilityState)
    );

    const sseStreamWithRetry$ = this.#createEventSource$().pipe(
      retry({
        delay: (_error, retryCount) => {
          const delayMs = Math.min(INITIAL_RECONNECT_DELAY_MS * Math.pow(2, retryCount - 1), MAX_RECONNECT_DELAY_MS);
          return timer(delayMs);
        },
        resetOnSuccess: true,
      })
    );

    return visibility$.pipe(
      switchMap((state) => (state === 'visible' ? sseStreamWithRetry$ : EMPTY)),
      takeUntil(this.#destroy$)
    );
  }

  #handleEvent(type: string, messageEvent: MessageEvent): void {
    let parsed: DataTableEventV1SelectionChange | DataTableEventV1ActiveUsers;
    try {
      parsed = JSON.parse(messageEvent.data) as DataTableEventV1SelectionChange | DataTableEventV1ActiveUsers;
    } catch (error) {
      ExceptionHandler.captureException(
        new InVivoError('Failed to parse datatable realtime event', {
          cause: error instanceof Error ? error : new Error(String(error)),
          slug: 'datatable-realtime-parse',
          level: 'warning',
          context: { studyApiId: this.#studyApiId, dataTableApiId: this.#dataTableApiId, eventType: type },
        })
      );
      return;
    }

    switch (type) {
      case 'selection_change':
        this.#eventEmitter.dispatchEvent(new CustomEvent(DataTableEvent.RemoteSelectionChange, { detail: parsed }));
        break;
      case 'active_users':
        this.#eventEmitter.dispatchEvent(new CustomEvent(DataTableEvent.ActiveUsersChange, { detail: parsed }));
        break;
    }
  }
}
